import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listDevices, createDevice, toggleDevice, reportDeviceHealth, updateDevice, deleteDevice } from "@/lib/devices.functions";
import { dispatchDeviceCommand } from "@/lib/devices/dispatch.functions";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Printer, Zap, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  probeBrowserPrint,
  resolvePrinter,
  sendZpl,
  buildTestLabelZpl,
  type AgentStatus,
} from "@/lib/printing/browser-print";

export const Route = createFileRoute("/_authenticated/app/admin/devices")({
  component: DevicesAdmin,
});

function DevicesAdmin() {
  const qc = useQueryClient();
  const { tenantId } = useScope();
  const listFn = useServerFn(listDevices);
  const createFn = useServerFn(createDevice);
  const toggleFn = useServerFn(toggleDevice);
  const dispatchFn = useServerFn(dispatchDeviceCommand);
  const reportHealthFn = useServerFn(reportDeviceHealth);
  const updateFn = useServerFn(updateDevice);
  const deleteFn = useServerFn(deleteDevice);

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"diverter" | "put_wall" | "printer" | "scanner" | "scale" | "other">("diverter");
  const [vendor, setVendor] = useState("hytrol");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [packStation, setPackStation] = useState("");

  type DeviceKind = typeof kind;
  type EditState = { id: string; kind: DeviceKind; vendor: string; name: string; address: string; packStation: string } | null;
  const [editing, setEditing] = useState<EditState>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["devices", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const [agent, setAgent] = useState<AgentStatus | null>(null);
  const [printingId, setPrintingId] = useState<string | null>(null);

  const refreshAgent = async () => {
    const status = await probeBrowserPrint();
    setAgent(status);
    return status;
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const status = await probeBrowserPrint();
      if (cancelled) return;
      setAgent(status);
      // Heartbeat any printer rows we can see the agent supports.
      if (status.ok && tenantId) {
        for (const d of data?.devices ?? []) {
          if (d.kind !== "printer") continue;
          const match = resolvePrinter(status, { name: d.name, address: d.address });
          if (match) {
            try { await reportHealthFn({ data: { id: d.id, healthy: true, detail: `agent:${match.name}` } }); }
            catch { /* non-fatal */ }
          }
        }
        qc.invalidateQueries({ queryKey: ["devices", tenantId] });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.devices?.length, tenantId]);

  const onCreate = async () => {
    if (!tenantId || !name || !address) return;
    try {
      await createFn({ data: { tenantId, kind, vendor, name, address, packStation: packStation.trim() || null } });
      toast.success("Device registered");
      setOpen(false);
      setName(""); setAddress(""); setPackStation("");
      qc.invalidateQueries({ queryKey: ["devices", tenantId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const onToggle = async (id: string, enabled: boolean) => {
    await toggleFn({ data: { id, enabled } });
    qc.invalidateQueries({ queryKey: ["devices", tenantId] });
  };

  const onSaveEdit = async () => {
    if (!editing) return;
    try {
      await updateFn({ data: { id: editing.id, kind: editing.kind, vendor: editing.vendor, name: editing.name, address: editing.address, packStation: editing.packStation.trim() || null } });
      toast.success("Device updated");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["devices", tenantId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm("Delete this device?")) return;
    try {
      await deleteFn({ data: { id } });
      toast.success("Device deleted");
      qc.invalidateQueries({ queryKey: ["devices", tenantId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const onTest = async (id: string, vendor: string) => {
    const payload =
      vendor.toLowerCase() === "hytrol" ? { lane: 1, cartonId: "test", release: false } :
      vendor.toLowerCase().startsWith("lightning") ? { slot: "A1", qty: 1, color: "green" } :
      { taskId: "test", operatorId: "demo", location: "A-01", sku: "TEST", qty: 1 };
    try {
      const r = await dispatchFn({ data: { deviceId: id, command: "test", payload } });
      if ((r as { ok: boolean }).ok) toast.success("Dispatched");
      else toast.error((r as { error?: string }).error ?? "Dispatch failed");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Dispatch failed");
    }
  };

  const onTestPrint = async (device: { id: string; name: string; address: string }) => {
    if (!tenantId) return;
    setPrintingId(device.id);
    try {
      const status = agent?.ok ? agent : await refreshAgent();
      if (!status.ok) {
        toast.error("Zebra Browser Print agent unreachable on https://localhost:9101");
        await reportHealthFn({ data: { id: device.id, healthy: false, detail: status.error } }).catch(() => {});
        return;
      }
      const printer = resolvePrinter(status, { name: device.name, address: device.address });
      if (!printer) {
        toast.error("No matching Zebra printer found on this workstation");
        return;
      }
      const zpl = buildTestLabelZpl({ deviceName: device.name, tenantId });
      const res = await sendZpl(printer, zpl);
      if (res.ok) {
        toast.success(`Test label sent to ${printer.name}`);
        await reportHealthFn({ data: { id: device.id, healthy: true, detail: `test:${printer.name}` } }).catch(() => {});
        qc.invalidateQueries({ queryKey: ["devices", tenantId] });
      } else {
        toast.error(`Print failed: ${res.error}`);
        await reportHealthFn({ data: { id: device.id, healthy: false, detail: res.error } }).catch(() => {});
      }
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Devices</h2>
          <p className="text-sm text-muted-foreground">Conveyor diverters, put-walls, and other connected hardware.</p>
        </div>
        <div className="flex items-center gap-3">
          {agent && (
            <div className="text-xs text-muted-foreground">
              Zebra agent:{" "}
              {agent.ok ? (
                <span className="text-foreground">{agent.printers.length} printer{agent.printers.length === 1 ? "" : "s"}</span>
              ) : (
                <span className="text-destructive">not detected</span>
              )}
            </div>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" />Register device</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Register device</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Kind</Label>
                <Select value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="diverter">Diverter</SelectItem>
                    <SelectItem value="put_wall">Put-wall</SelectItem>
                    <SelectItem value="printer">Printer</SelectItem>
                    <SelectItem value="scanner">Scanner</SelectItem>
                    <SelectItem value="scale">Scale</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vendor</Label>
                <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="hytrol / lightning-pick" />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="lane-A-divert" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="mqtt://broker.local:1883/hytrol/laneA or https://..." />
              </div>
              <div>
                <Label>Pack station (optional)</Label>
                <Input value={packStation} onChange={(e) => setPackStation(e.target.value)} placeholder="Pack Station #1" />
              </div>
            </div>
            <DialogFooter><Button onClick={onCreate}>Register</Button></DialogFooter>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Pack station</TableHead>
              <TableHead>Health</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead className="w-24">Test</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={9} className="text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!isLoading && (data?.devices ?? []).length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-muted-foreground">No devices yet.</TableCell></TableRow>
            )}
            {(data?.devices ?? []).map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell>{d.kind}</TableCell>
                <TableCell>{d.vendor}</TableCell>
                <TableCell className="font-mono text-xs">{d.address}</TableCell>
                <TableCell className="text-xs">{d.pack_station ?? <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell>
                  <Badge variant={d.health.healthy ? "default" : "secondary"}>
                    {d.health.healthy ? "healthy" : "unknown"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Switch checked={d.enabled} onCheckedChange={(v) => onToggle(d.id, v)} />
                </TableCell>
                <TableCell>
                  {d.kind === "printer" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!d.enabled || printingId === d.id}
                      onClick={() => onTestPrint({ id: d.id, name: d.name, address: d.address })}
                    >
                      <Printer className="h-3.5 w-3.5 mr-1" />
                      {printingId === d.id ? "Sending…" : "Test print"}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled={!d.enabled} onClick={() => onTest(d.id, d.vendor)}>
                      <Zap className="h-3.5 w-3.5 mr-1" />Send
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing({ id: d.id, kind: d.kind as DeviceKind, vendor: d.vendor, name: d.name, address: d.address, packStation: d.pack_station ?? "" })}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(d.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit device</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Kind</Label>
                <Select value={editing.kind} onValueChange={(v) => setEditing({ ...editing, kind: v as DeviceKind })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="diverter">Diverter</SelectItem>
                    <SelectItem value="put_wall">Put-wall</SelectItem>
                    <SelectItem value="printer">Printer</SelectItem>
                    <SelectItem value="scanner">Scanner</SelectItem>
                    <SelectItem value="scale">Scale</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vendor</Label>
                <Input value={editing.vendor} onChange={(e) => setEditing({ ...editing, vendor: e.target.value })} />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} placeholder="browser-print://PRINTER_NAME" />
              </div>
              <div>
                <Label>Pack station (optional)</Label>
                <Input value={editing.packStation} onChange={(e) => setEditing({ ...editing, packStation: e.target.value })} placeholder="Pack Station #1" />
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={onSaveEdit}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}